import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Grid2,
  Box,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
} from "@mui/material";
import LoadingButton from "../../components/ui/LoadingButton";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { toast } from "react-toastify";
import api from "../../services/api";
import { useIntl } from "../../contexts/IntlContext";
import { CompactTableContainer } from "../../components/table/CompactTableContainer";
import StatusPill from "../../components/ui/StatusPill";
import { getSubscriptionStatusLabel, getSubscriptionStatusLevel } from "./subscriptionStatus";
import { SubscriptionRow } from "./types";

type DetailData = {
  transaction: SubscriptionRow;
  visitor: Record<string, unknown> | null;
  history: SubscriptionRow[];
};

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  transactionId: string | null;
};

function formatCurrency(value: number): string {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusChip(status: string, intl: ReturnType<typeof useIntl>) {
  return (
    <StatusPill
      size="small"
      level={getSubscriptionStatusLevel(status)}
      label={getSubscriptionStatusLabel(intl, status)}
    />
  );
}

function formatTransactionType(type: string, intl: ReturnType<typeof useIntl>) {
  return intl.formatMessage({
    id: type === "subscription" ? "subscriptionDetail.type.subscription" : "subscriptionDetail.type.singlePurchase",
  });
}

function formatBillingCycle(billingCycle: string | null, intl: ReturnType<typeof useIntl>) {
  if (billingCycle === "monthly_recurring") return intl.formatMessage({ id: "subscriptionDetail.cycle.monthly" });
  if (billingCycle === "annual") return intl.formatMessage({ id: "subscriptionDetail.cycle.annual" });
  return "-";
}

export function SubscriptionDetailDialog({ open, setOpen, transactionId }: Props) {
  const intl = useIntl();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [data, setData] = useState<DetailData | null>(null);
  const [loading, setLoading] = useState(false);
  const [togglingTest, setTogglingTest] = useState(false);

  useEffect(() => {
    if (!open || !transactionId) return;

    setLoading(true);
    api
      .get(`/app/admin/subscriptions/${transactionId}`)
      .then((response) => {
        setData(response.data);
      })
      .catch((error) => {
        if (error?.response?.status === 400 || error?.response?.status === 401) {
          toast.error(error.response.data.message);
        } else {
          toast.error(intl.formatMessage({ id: "messages.connectionError" }));
        }
      })
      .finally(() => setLoading(false));
  }, [open, transactionId, intl]);

  const handleClose = () => {
    setOpen(false);
    setData(null);
  };

  const tx = data?.transaction;

  const handleToggleTest = async () => {
    if (!tx) return;
    setTogglingTest(true);
    try {
      const response = await api.patch(`/app/admin/subscriptions/${tx.transactionId}/test`, {
        isTest: !tx.isTest,
      });
      setData((prev) => (prev ? { ...prev, transaction: response.data } : prev));
      toast.success(intl.formatMessage({ id: "subscriptionDetail.testFlagUpdateSuccess" }));
    } catch (error: any) {
      const message =
        error?.response?.status === 400 || error?.response?.status === 401
          ? error.response.data.message
          : intl.formatMessage({ id: "subscriptionDetail.testFlagUpdateError" });
      toast.error(message);
    } finally {
      setTogglingTest(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      aria-labelledby="subscription-detail-title"
    >
      <DialogTitle id="subscription-detail-title">
        {intl.formatMessage({ id: "subscriptionDetail.title" })}
      </DialogTitle>
      <DialogContent>

        {loading && <Typography>{intl.formatMessage({ id: "common.loading" })}...</Typography>}

        {data && tx && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {intl.formatMessage({ id: "subscriptionDetail.visitorInfo" })}
              </Typography>
              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "common.name" })}</Typography>
                  <Typography variant="body1">{tx.visitorName || "-"}</Typography>
                </Grid2>
                <Grid2 size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "common.email" })}</Typography>
                  <Typography variant="body1">{tx.visitorEmail || "-"}</Typography>
                </Grid2>
                {data.visitor && (
                  <>
                    <Grid2 size={{ xs: 6 }}>
                      <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "subscriptionDetail.phone" })}</Typography>
                      <Typography variant="body1">{(data.visitor.phone as string) || "-"}</Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 6 }}>
                      <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "common.establishmentName" })}</Typography>
                      <Typography variant="body1">{(data.visitor.establishmentName as string) || "-"}</Typography>
                    </Grid2>
                  </>
                )}
              </Grid2>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {intl.formatMessage({ id: "subscriptionDetail.transactionData" })}
              </Typography>
              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 6, sm: 4 }}>
                  <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "subscriptionDetail.type" })}</Typography>
                  <Typography variant="body1">
                    {formatTransactionType(tx.type, intl)}
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 4 }}>
                  <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "subscriptionDetail.product" })}</Typography>
                  <Typography variant="body1" sx={{ textTransform: "capitalize" }}>
                    {tx.offering}
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 4 }}>
                  <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "subscriptionDetail.cycle" })}</Typography>
                  <Typography variant="body1">
                    {formatBillingCycle(tx.billingCycle, intl)}
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 4 }}>
                  <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "subscriptionDetail.value" })}</Typography>
                  <Typography variant="body1">{formatCurrency(tx.amount)}</Typography>
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 4 }}>
                  <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "common.status" })}</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    {statusChip(tx.status, intl)}
                    {tx.isInTrial && (
                      <Chip size="small" variant="outlined" color="info" label={intl.formatMessage({ id: "subscriptionDetail.trialChip" })} />
                    )}
                    {tx.isTest && (
                      <Chip size="small" variant="outlined" color="warning" label={intl.formatMessage({ id: "subscriptionDetail.testChip" })} />
                    )}
                  </Stack>
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 4 }}>
                  <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "subscriptionDetail.purchaseDate" })}</Typography>
                  <Typography variant="body1">{formatDate(tx.createdAt)}</Typography>
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 4 }}>
                  <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "subscriptionDetail.nextBilling" })}</Typography>
                  <Typography variant="body1">{formatDate(tx.nextBillingDate)}</Typography>
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 4 }}>
                  <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "subscriptionDetail.cancellation" })}</Typography>
                  <Typography variant="body1">{formatDate(tx.cancellationCompletedAt)}</Typography>
                </Grid2>
              </Grid2>
            </Box>

            <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <LoadingButton
                onClick={handleToggleTest}
                loading={togglingTest}
                size="small"
                color={tx.isTest ? "primary" : "warning"}
                variant="outlined"
              >
                {intl.formatMessage({ id: tx.isTest ? "subscriptionDetail.unmarkAsTest" : "subscriptionDetail.markAsTest" })}
              </LoadingButton>
              {tx.isTest && tx.isTestMarkedAt && (
                <Typography variant="caption" color="text.secondary">
                  {intl.formatMessage(
                    { id: "subscriptionDetail.testMarkedBy" },
                    { name: tx.isTestMarkedByName || tx.isTestMarkedBy || "-", date: formatDate(tx.isTestMarkedAt) },
                  )}
                </Typography>
              )}
            </Box>

            <Accordion sx={{ mb: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {intl.formatMessage({ id: "subscriptionDetail.technicalDetails" })}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "subscriptionDetail.internalTransactionId" })}</Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>{tx.transactionId}</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "subscriptionDetail.externalId" })}</Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>{tx.externalId || "-"}</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "subscriptionDetail.gatewayPaymentId" })}</Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>{tx.gatewayPaymentId || "-"}</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "subscriptionDetail.subscriptionExternalId" })}</Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>{tx.subscriptionExternalId || "-"}</Typography>
                  </Grid2>
                </Grid2>
              </AccordionDetails>
            </Accordion>

            {data.history.length > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {intl.formatMessage({ id: "subscriptionDetail.historyTitle" })}
                </Typography>
                <CompactTableContainer>
                  <TableHead>
                      <TableRow>
                        <TableCell>{intl.formatMessage({ id: "subscriptionDetail.purchaseDate" })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: "subscriptionDetail.type" })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: "subscriptionDetail.product" })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: "subscriptionDetail.value" })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: "common.status" })}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.history.map((item) => (
                        <TableRow hover key={item.transactionId}>
                          <TableCell>{formatDate(item.createdAt)}</TableCell>
                          <TableCell>
                            {formatTransactionType(item.type, intl)}
                          </TableCell>
                          <TableCell sx={{ textTransform: "capitalize" }}>{item.offering}</TableCell>
                          <TableCell>{formatCurrency(item.amount)}</TableCell>
                          <TableCell>{statusChip(item.status, intl)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                </CompactTableContainer>
              </Box>
            )}
          </>
        )}

        {!loading && !data && transactionId && (
          <Typography>{intl.formatMessage({ id: "subscriptionDetail.noDataFound" })}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={handleClose} size="small" color="primary" autoFocus>
          {intl.formatMessage({ id: "common.close" })}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
